import { isDefined } from '@fabric-app/utils/guards';
import { createStyledClone } from './createStyledClone.js';
import { generateSelectorPath } from '@fabric-app/utils/element';

const blacklistedSelectors = [
  ".ProseMirror-widget"
];
const parentSelectors = {
  table: {
    selector: "table",
    deep: true
  },
  codeBlock: {
    selector: "pre",
    deep: true
  }
};
const findRootNode = (element, rootNode) => {
  let root = element.closest(rootNode);
  while (root && root.parentElement) {
    const parentMatch = root.parentElement.closest(rootNode);
    if (!parentMatch) break;
    root = parentMatch;
  }
  return root;
};
const getParentNodes = (node, viewDom, nodeType) => {
  const parentNodes = [];
  const stopSelector = parentSelectors[nodeType];
  if (!stopSelector) {
    return {
      nodes: parentNodes,
      selector: null
    };
  }
  let currentNode = node.parentElement;
  while (isDefined(currentNode) && currentNode !== viewDom && currentNode !== document.body && isDefined(currentNode.parentElement)) {
    parentNodes.push(currentNode);
    if (currentNode.matches(stopSelector.selector)) {
      break;
    }
    currentNode = currentNode.parentElement;
  }
  return {
    nodes: parentNodes,
    selector: stopSelector
  };
};
const cleanupDragPreviewElement = (element) => {
  blacklistedSelectors.forEach((selector) => {
    element.querySelectorAll(selector).forEach((el) => el.remove());
  });
  element.querySelectorAll(".ProseMirror-yjs-selection").forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.backgroundColor = "transparent";
    }
  });
  return element;
};
const generatePreview = (element, viewDom, nodeType) => {
  const { nodes: parentNodes, selector } = getParentNodes(element, viewDom, nodeType);
  if (!selector) {
    return createSimplePreview(element);
  }
  const rootNode = selector.rootNode ? findRootNode(element, selector.rootNode) : null;
  if (rootNode) {
    return createRootBasedPreview(element, rootNode);
  }
  if (selector.deep) {
    return createDeepClonePreview(parentNodes, selector.selector);
  }
  return createHierarchyPreview(parentNodes, element);
};
const cleanupDragPreviews = () => {
  document.querySelectorAll('[data-drag-handle-preview="true"]').forEach((el) => el.remove());
};
const createPreviewContainer = () => {
  cleanupDragPreviews();
  const container = document.createElement("div");
  container.dataset.dragHandlePreview = "true";
  container.style.position = "absolute";
  container.style.left = "-10000px";
  container.style.top = "-10000px";
  container.style.pointerEvents = "none";
  container.style.padding = "0.1rem";
  return container;
};
const createSimplePreview = (element) => {
  const clone = createStyledClone(element);
  if (!(clone instanceof Element)) {
    return null;
  }
  const container = document.createElement("div");
  container.appendChild(clone);
  return cleanupDragPreviewElement(container);
};
const createRootBasedPreview = (element, rootNode) => {
  const elementSelector = generateSelectorPath(rootNode, element);
  const clone = createStyledClone(rootNode, true);
  if (!(clone instanceof Element)) {
    return null;
  }
  const clonedElement = clone.querySelector(elementSelector);
  if (!clonedElement) {
    return null;
  }
  clone.querySelectorAll("*").forEach((el) => {
    if (el instanceof HTMLElement) {
      if (el !== clonedElement && !el.contains(clonedElement) && !clonedElement.contains(el)) {
        el.style.position = "absolute";
        el.style.opacity = "0";
      } else if (el !== clonedElement && el.contains(clonedElement)) {
        el.style.margin = "0";
        el.style.padding = "0";
        el.style.gap = "0";
        el.style.borderSpacing = "0";
        el.style.columnGap = "0";
        el.style.rowGap = "0";
        const style = document.createElement("style");
        const id = el.id || el.getAttribute("data-uuid");
        style.textContent = `
          ${el.tagName}#${id}::before,
          ${el.tagName}#${id}::after {
            opacity: 0;
            position: absolute;
          }
        `;
        el.appendChild(style);
      }
    }
  });
  const container = document.createElement("div");
  container.appendChild(clone);
  return cleanupDragPreviewElement(container);
};
const createDeepClonePreview = (parentNodes, selector) => {
  const matchingParentIndex = parentNodes.findIndex((node) => node.matches(selector));
  if (matchingParentIndex >= 0) {
    const matchingParent = parentNodes[matchingParentIndex];
    const clone = createStyledClone(matchingParent, true);
    if (!(clone instanceof Element)) {
      return null;
    }
    const container = document.createElement("div");
    container.appendChild(clone);
    return cleanupDragPreviewElement(container);
  }
  return null;
};
const createHierarchyPreview = (parentNodes, element) => {
  let lastParent = null;
  const container = document.createElement("div");
  for (const node of parentNodes) {
    const clone = createStyledClone(node, false);
    if (!(clone instanceof Element)) {
      return null;
    }
    if (lastParent) {
      lastParent.appendChild(clone);
    } else {
      container.appendChild(clone);
    }
    lastParent = clone;
  }
  const clonedElement = createStyledClone(element);
  if (!(clonedElement instanceof Element)) {
    return null;
  }
  if (lastParent) {
    lastParent.appendChild(clonedElement);
  } else {
    container.appendChild(clonedElement);
  }
  return cleanupDragPreviewElement(container);
};

export { blacklistedSelectors, cleanupDragPreviewElement, createPreviewContainer, generatePreview };
//# sourceMappingURL=generatePreview.js.map
